import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { getEmployeeByUserId, getEmployees } from "../data/redux/employee/employeeAction";
import { AppDispatch } from "../data/redux/store";
import { fetchTicketAllCategories, fetchTicketCategorySummary, fetchTicketCategorySummaryByDepartment } from "../data/redux/tickets/ticketCategoryActions";
import { createTicket, fetchAllTickets, fetchTicketsByDepartment, fetchTicketsByEmployeeHierarchy, fetchTicketsSummaryByDepartment, fetchTicketSummary } from "../data/redux/tickets/ticket/ticketActions";
import { Drawer, message } from "antd";
import { fetchAllDepartments } from "../data/redux/department/departmentAction";
import { filterByStatus } from "../utils/filterActiveData";

interface CategoryOption {
  ticket_category_id: number;
  category_name: string;
}

interface EmployeeOption {
  EmployeeID: number;
  Name: string;
}

interface Priority {
  value: string;
  label: string;
}

interface Status {
  value: string;
  label: string;
}

interface Department {
  Id: number;
  Name: string;
}

const TicketListModal: React.FC<any> = ({ handleCancel, isModalVisible }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: any) => state.auth);
  const { employees } = useSelector((state: any) => state.employee);
  const { TicketsCategories } = useSelector((state: any) => state.ticket.ticketCategory);
  const { departments } = useSelector((state: any) => state.department);
  const { employeesbyUser } = useSelector((state: any) => state.employee);
  const { tickets, loading } = useSelector((state: any) => state.ticket.createTickit);
  const activeTicketCategory = filterByStatus(TicketsCategories, 'active', 'status');
  const activeDepartments = filterByStatus(departments, 'active', 'Status');
  const activeAssignTo = filterByStatus(employees, 'active', 'employee_status');

  const [formData, setFormData] = useState({
    title: '',
    category: null as CategoryOption | null,
    assignTo: null as EmployeeOption | null,
    description: '',
    priority: null as Priority | null,
    status: null as Status | null,
    department: null as Department | null,
    ticketDate: new Date().toISOString().split('T')[0],
    document: null as File | null,
  });

  const [errors, setErrors] = useState({
    title: '',
    category: '',
    assignTo: '',
    description: '',
    priority: '',
    status: '',
    department: '',
    ticketDate: ''
  });

  const resetForm = () => {
    setFormData({
      title: '',
      category: null,
      assignTo: null,
      description: '',
      priority: null,
      status: null,
      department: null,
      ticketDate: '',
      document: null
    });
    setErrors({
      title: '',
      category: '',
      assignTo: '',
      description: '',
      priority: '',
      status: '',
      department: '',
      ticketDate: ''
    });
  };
  const filteredAssignTo = useMemo(() => {
    if (!formData.department) return activeAssignTo; // Show all if no department selected
    return activeAssignTo.filter(
      (emp: any) => emp.DepartmentId === formData.department?.Id
    );
  }, [formData.department, activeAssignTo]);

  const priority = [
    { value: "Select", label: "Select" },
    { value: "0", label: "High" },
    { value: "1", label: "Medium" },
    { value: "2", label: "Low" },
  ];

  const status = [
    { value: "Open", label: "Open" },
    { value: "Pending", label: "Pending" },
    { value: "Solved", label: "Solved" },
    { value: "Discussion", label: "Discussion" },
    { value: "Development", label: "Development" },
    { value: "Fixed", label: "Fixed" },
    { value: "Testing", label: "Testing" },
    { value: "Closed", label: "Closed" },
  ];

  useEffect(() => {
    if (!employees.length) dispatch(getEmployees());
    if (!TicketsCategories.length) dispatch(fetchTicketAllCategories());
    if (!departments.length) dispatch(fetchAllDepartments());
    if (!employeesbyUser?.employeeId) {
      dispatch(getEmployeeByUserId({ userId: user?.UserId }));
    }
  }, [dispatch]);

  // const handleChange = (field: string, value: any) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleChange = (field: string, value: any) =>
    setFormData((prev) => ({ ...prev, [field]: value }));


  const validateForm = () => {
    const fieldValidations: { [key: string]: string } = {
      title: 'Subject is required',
      category: 'Ticket Category is required',
      // assignTo: 'Assign To is required',
      priority: 'Priority is required',
      // status: 'Status is required',
      department: 'Department is required',
      ticketDate: 'Ticket Date is required',
    };
    const newErrors = Object.keys(fieldValidations).reduce((acc, field) => {
      acc[field as keyof typeof formData] = formData[field as keyof typeof formData]
        ? ''
        : fieldValidations[field];
      return acc;
    }, {} as { [key in keyof typeof formData]: string });
    setErrors(newErrors);
    return Object.values(newErrors).every(error => error === '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const formPayload: any = new FormData();
      formPayload.append('ticket_category_id', formData.category?.ticket_category_id ?? '');
      formPayload.append('Request', employeesbyUser?.employeeId ?? '');
      formPayload.append('TicketDate', formData.ticketDate);
      formPayload.append('EmployeeId', formData.assignTo?.EmployeeID ?? '');
      formPayload.append('DepartmentId', formData.department?.Id ?? '');
      formPayload.append('SupervisorId', '');
      formPayload.append('ticket_status', formData.status?.value ?? 'Open');
      formPayload.append('CreatedBy', employeesbyUser?.employeeId ?? user?.UserId);
      
      formPayload.append('ticketDetail[title]', formData.title);
      formPayload.append('ticketDetail[Response]', formData.description);
      formPayload.append('ticketDetail[ResponseBy]', employeesbyUser?.employeeId ?? '');
      formPayload.append('ticketDetail[CreatedBy]', user?.UserId ?? '');
      formPayload.append('ticketDetail[AssignedTo]', formData.assignTo?.EmployeeID ?? '');
      formPayload.append('ticketDetail[Description]', formData.description);
      formPayload.append('ticketDetail[Priority]', formData.priority?.value ?? '0');

      if (formData.document) {
        formPayload.append('document', formData.document);
      }

      const result = await dispatch(createTicket(formPayload));
      if (result.meta.requestStatus === 'fulfilled') {
        await dispatch(fetchAllTickets())
        await dispatch(fetchTicketSummary())
        await dispatch(fetchTicketCategorySummary())
        await dispatch(fetchTicketsByDepartment(employeesbyUser?.DepartmentId))
        await dispatch(fetchTicketsSummaryByDepartment(employeesbyUser?.DepartmentId))
        await dispatch(fetchTicketCategorySummaryByDepartment(employeesbyUser?.DepartmentId))
        if (employeesbyUser?.employeeId && employeesbyUser?.DepartmentId) {
          dispatch(fetchTicketsByEmployeeHierarchy({
            employeeId: employeesbyUser.employeeId,
            departmentId: employeesbyUser.DepartmentId
          }));
        }
        message.success(result.payload.message);
        resetForm();
        handleCancel();
        setErrors({
          title: '',
          category: '',
          assignTo: '',
          description: '',
          priority: '',
          status: '',
          department: '',
          ticketDate: ''
        })
      } else if (result.meta.requestStatus === 'rejected') {
        message.error(result.payload.error);
      }
    }
  };

  const renderSelectField = (label: string, fieldName: string, options: any[], value: any, error: string, onChange: (value: any) => void, isRequired = true) => (
    <div className="mb-3">
      {label} {isRequired && <span className="text-danger"> *</span>}
      <Select
        className="select"
        options={options
          ?.slice()
          .sort((a: any, b: any) => {
            const labelA = (a.label || a.Name || a.category_name || "").toString();
            const labelB = (b.label || b.Name || b.category_name || "").toString();
            return labelA.localeCompare(labelB, undefined, { sensitivity: 'base' });
          })}
        value={value}
        onChange={(selectedOption: any) => onChange(selectedOption)}
        getOptionLabel={(e: any) => e.label || e.Name || e.category_name}
        getOptionValue={(e: any) => e.value || e.EmployeeID || e.ticket_category_id || e.Id}
      />

      {error && <div className="text-danger">{error}</div>}
    </div>
  );

  return (
    <Drawer
      title="Add Tickets"
      placement="right"
      width={700}
      onClose={() => {
        handleCancel();
        resetForm();
      }}
      visible={isModalVisible}
      style={{ marginTop: 50, paddingBottom: 50 }}
    >
      <form onSubmit={handleSubmit}>
        <div className="modal-body">
          <div className="row">
            <div className="col-md-12">
              <div className="mb-3">
                <label className="form-label">Subject <span className="text-danger"> *</span></label>
                <input type="text" className="form-control" placeholder="Enter Subject" value={formData.title} onChange={(e) => handleChange('title', e.target.value)} />
                {errors.title && <div className="text-danger">{errors.title}</div>}
              </div>

              {renderSelectField('Ticket Category', 'category', activeTicketCategory, formData.category, errors.category, (value: any) => handleChange('category', value))}
              {renderSelectField('Select Department', 'department', activeDepartments, formData.department, errors.department, (value: any) => handleChange('department', value))}
              {renderSelectField('Assign To', 'assignTo', filteredAssignTo, formData.assignTo, errors.assignTo, (value: any) => handleChange('assignTo', value), false)}
              <div className="mb-3">
                <label className="form-label">Ticket Date <span className="text-danger"> *</span></label>
                <input type="date" min={new Date().toISOString().split('T')[0]} className="form-control" value={formData.ticketDate} onChange={(e) => handleChange('ticketDate', e.target.value)} />
                {errors.ticketDate && <div className="text-danger">{errors.ticketDate}</div>}
              </div>

              {renderSelectField('Priority', 'priority', priority, formData.priority, errors.priority, (value: any) => handleChange('priority', value))}
              {/* {renderSelectField('Status', 'status', status, formData.status, errors.status, (value: any) => handleChange('status', value))} */}

              <div className="mb-3">
                <label className="form-label">Ticket Description </label>
                <textarea className="form-control" placeholder="Add Description" value={formData.description} onChange={(e) => handleChange('description', e.target.value)} />
                {errors.description && <div className="text-danger">{errors.description}</div>}
              </div>
              <div className="mb-3">
                <input
                  type="file"
                  className="form-control-file"
                  style={{ cursor: 'pointer', opacity: 10 }}
                  onChange={(e) => handleChange('document', e.target.files?.[0] || null)}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <Link to="#" className="btn btn-light me-2" onClick={() => { handleCancel(); resetForm(); }}>Cancel</Link>
          <button type="submit" className="btn btn-primary" disabled={loading} >Add Ticket</button>
        </div>
      </form>
    </Drawer >
  );
};

export default TicketListModal;
